import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BellIcon, XIcon, CoffeeIcon, UtensilsIcon, GlassWaterIcon, PrinterIcon, CarIcon, PhoneIcon, ClockIcon, CheckCircle2Icon, SendIcon } from 'lucide-react';
interface ButlerCallProps {
  isOpen: boolean;
  onClose: () => void;
}
const quickRequests = [{
  id: 1,
  icon: <CoffeeIcon className="w-6 h-6" data-id="element-1" />,
  label: 'Coffee/Tea',
  color: '#8B4513'
}, {
  id: 2,
  icon: <GlassWaterIcon className="w-6 h-6" data-id="element-2" />,
  label: 'Beverages',
  color: '#60A5FA'
}, {
  id: 3,
  icon: <UtensilsIcon className="w-6 h-6" data-id="element-3" />,
  label: 'Refreshments',
  color: '#34D399'
}, {
  id: 4,
  icon: <PrinterIcon className="w-6 h-6" data-id="element-4" />,
  label: 'Print Service',
  color: '#A78BFA'
}, {
  id: 5,
  icon: <CarIcon className="w-6 h-6" data-id="element-5" />,
  label: 'Car Service',
  color: '#F472B6'
}, {
  id: 6,
  icon: <PhoneIcon className="w-6 h-6" data-id="element-6" />,
  label: 'Call Assistant',
  color: '#FBBF24'
}];
export function ButlerCall({
  isOpen,
  onClose
}: ButlerCallProps) {
  const [selectedRequest, setSelectedRequest] = useState<number | null>(null);
  const [customMessage, setCustomMessage] = useState('');
  const [requestSent, setRequestSent] = useState(false);
  const handleSendRequest = () => {
    setRequestSent(true);
    setTimeout(() => {
      setRequestSent(false);
      setSelectedRequest(null);
      setCustomMessage('');
      onClose();
    }, 2000);
  };
  const handleClose = () => {
    setSelectedRequest(null);
    setCustomMessage('');
    setRequestSent(false);
    onClose();
  };
  return <AnimatePresence data-id="element-7">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={handleClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" data-id="element-8" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="fixed inset-0 flex items-center justify-center z-50 p-8" data-id="element-9">
            <div className="w-full max-w-2xl glass-card rounded-3xl p-8 border border-[rgba(201,168,76,0.3)]" data-id="element-10">
              {/* Header */}
              <div className="flex items-center justify-between mb-8" data-id="element-11">
                <div className="flex items-center gap-4" data-id="element-12">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#C9A84C] to-[#D4AF37] flex items-center justify-center shadow-lg shadow-[rgba(201,168,76,0.3)]" data-id="element-13">
                    <BellIcon className="w-7 h-7 text-[#0D0F14]" data-id="element-14" />
                  </div>
                  <div data-id="element-15">
                    <h2 className="text-2xl font-semibold font-['Playfair_Display'] text-white" data-id="element-16">
                      Butler Service
                    </h2>
                    <p className="text-sm text-gray-400" data-id="element-17">
                      How may we assist you?
                    </p>
                  </div>
                </div>
                <button onClick={handleClose} className="w-12 h-12 rounded-xl bg-[rgba(255,255,255,0.05)] hover:bg-[rgba(255,255,255,0.1)] flex items-center justify-center transition-colors" data-id="element-18">
                  <XIcon className="w-6 h-6 text-gray-400" data-id="element-19" />
                </button>
              </div>

              <AnimatePresence mode="wait" data-id="element-20">
                {requestSent ? <motion.div key="success" initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.9
            }} className="py-16 text-center" data-id="element-21">
                    <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                type: 'spring',
                stiffness: 200
              }} className="w-20 h-20 rounded-full bg-[rgba(34,197,94,0.15)] border-2 border-green-500 flex items-center justify-center mx-auto mb-6" data-id="element-22">
                      <CheckCircle2Icon className="w-10 h-10 text-green-500" data-id="element-23" />
                    </motion.div>
                    <h3 className="text-xl font-semibold text-white mb-2" data-id="element-24">
                      Request Sent
                    </h3>
                    <p className="text-gray-400" data-id="element-25">
                      Your butler will attend to you shortly
                    </p>
                    <div className="flex items-center justify-center gap-2 mt-4 text-sm text-gray-500" data-id="element-26">
                      <ClockIcon className="w-4 h-4" data-id="element-27" />
                      <span data-id="element-28">Estimated arrival: 2-3 minutes</span>
                    </div>
                  </motion.div> : <motion.div key="form" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} data-id="element-29">
                    {/* Quick Requests Grid */}
                    <div className="grid grid-cols-3 gap-4 mb-8" data-id="element-30">
                      {quickRequests.map(request => <motion.button key={request.id} whileTap={{
                  scale: 0.98
                }} onClick={() => setSelectedRequest(request.id)} className={`p-6 rounded-2xl transition-all touch-button h-auto flex-col ${selectedRequest === request.id ? 'border-2' : 'bg-[rgba(255,255,255,0.03)] hover:bg-[rgba(255,255,255,0.06)]'}`} style={selectedRequest === request.id ? {
                  backgroundColor: `${request.color}15`,
                  borderColor: `${request.color}50`
                } : {}} data-id="element-31">
                          <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-3" style={{
                    backgroundColor: selectedRequest === request.id ? `${request.color}30` : 'rgba(255,255,255,0.05)',
                    color: selectedRequest === request.id ? request.color : '#9CA3AF'
                  }} data-id="element-32">
                            {request.icon}
                          </div>
                          <span className={`text-sm font-medium ${selectedRequest === request.id ? 'text-white' : 'text-gray-400'}`} data-id="element-33">
                            {request.label}
                          </span>
                        </motion.button>)}
                    </div>

                    {/* Custom Message */}
                    <div className="mb-8" data-id="element-34">
                      <label className="block text-sm text-gray-400 mb-2" data-id="element-35">
                        Additional Instructions
                      </label>
                      <textarea value={customMessage} onChange={e => setCustomMessage(e.target.value)} placeholder="Any specific requirements or preferences..." className="w-full h-24 px-4 py-3 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(201,168,76,0.2)] text-white placeholder-gray-500 resize-none focus:outline-none focus:border-[rgba(201,168,76,0.5)] transition-colors" data-id="element-36" />
                    </div>

                    {/* Send Button */}
                    <button onClick={handleSendRequest} disabled={!selectedRequest} className={`w-full touch-button h-16 rounded-xl font-semibold text-lg transition-all ${selectedRequest ? 'bg-gradient-to-r from-[#C9A84C] to-[#D4AF37] text-[#0D0F14] hover:shadow-lg hover:shadow-[rgba(201,168,76,0.3)]' : 'bg-[rgba(255,255,255,0.05)] text-gray-500 cursor-not-allowed'}`} data-id="element-37">
                      <SendIcon className="w-5 h-5 mr-2" data-id="element-38" />
                      Send Request
                    </button>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}